/** Client ROI calculator config (roomrounds-roi-calculator.html) */

export const ROI_CALC_CONFIG = {
  tiers: [
    {
      name: "Small (4–50 beds)",
      maxBeds: 50,
      base: 250,
      maintenance: 75,
      support: 25,
    },
    {
      name: "Medium (51–80 beds)",
      maxBeds: 80,
      base: 500,
      maintenance: 100,
      support: 50,
    },
    {
      name: "Large-Medium (81–125 beds)",
      maxBeds: 125,
      base: 700,
      maintenance: 125,
      support: 75,
    },
    {
      name: "Large (126–200 beds)",
      maxBeds: 200,
      base: 900,
      maintenance: 150,
      support: 100,
    },
  ],
  enterpriseName: "Enterprise (201+ beds)",
  manualHoursPerBedPerWeek: 1.2,
  efficiencyGain: 0.4,
  weeksPerMonth: 4,
  laborRatePerHour: 50,
  daysPerMonth: 30,
  moduleAdoption: 0.5,
} as const;

type PricingTier = (typeof ROI_CALC_CONFIG.tiers)[number];

export type RoiTierInfo =
  | { name: string; enterprise: true }
  | (PricingTier & { enterprise?: false });

export type RoiComputeResult = {
  tierName: string;
  savedWk: number;
  monthlySavings: number;
  enterprise: boolean;
  monthlyPrice?: number;
  paybackDays?: number | null;
};

function tierFor(beds: number): RoiTierInfo {
  for (const t of ROI_CALC_CONFIG.tiers) {
    if (beds <= t.maxBeds) return t;
  }
  return { name: ROI_CALC_CONFIG.enterpriseName, enterprise: true };
}

function effectivePrice(t: PricingTier): number {
  const { moduleAdoption } = ROI_CALC_CONFIG;
  return (
    t.base +
    moduleAdoption * t.maintenance +
    moduleAdoption * t.support
  );
}

export function computeRoi(beds: number): RoiComputeResult {
  const b = Math.max(1, Math.round(beds || 0));
  const manualWk = b * ROI_CALC_CONFIG.manualHoursPerBedPerWeek;
  const savedWk = manualWk * ROI_CALC_CONFIG.efficiencyGain;
  const moSavings =
    savedWk * ROI_CALC_CONFIG.weeksPerMonth * ROI_CALC_CONFIG.laborRatePerHour;
  const t = tierFor(b);

  const result: RoiComputeResult = {
    tierName: t.name,
    savedWk,
    monthlySavings: moSavings,
    enterprise: "enterprise" in t && !!t.enterprise,
  };

  if (!result.enterprise && !("enterprise" in t)) {
    const price = effectivePrice(t);
    const dailySavings = moSavings / ROI_CALC_CONFIG.daysPerMonth;
    result.monthlyPrice = price;
    result.paybackDays = dailySavings > 0 ? price / dailySavings : null;
  }

  return result;
}

export function formatRoiMoney(v: number): string {
  return `$${Math.round(v).toLocaleString()}`;
}
